import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Briefcase,
  Users,
  CheckCircle,
  DownloadSimple,
  Plus,
  ArrowRight,
  Clock,
  CheckSquare,
  XCircle,
  Spinner,
  Trash,
  Warning,
  Copy,
  DotsThree,
  ArrowsClockwise
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card className="glass-card border-slate-200 hover:border-slate-200 transition-colors duration-200">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-500 text-sm font-medium">{label}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{typeof value === "number" ? value.toLocaleString("en-US") : value}</p>
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon size={24} weight="fill" />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { bg: "bg-fuchsia-100", text: "text-fuchsia-700", icon: Clock },
    processing: { bg: "bg-blue-100", text: "text-blue-700", icon: Spinner },
    completed: { bg: "bg-emerald-100", text: "text-emerald-700", icon: CheckSquare },
    failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
    cancelled: { bg: "bg-slate-100", text: "text-slate-600", icon: XCircle }
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={14} className={status === "processing" ? "animate-spin" : ""} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const DashboardPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, job: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [stopping, setStopping] = useState(false);
  const failedCount = (stats?.recent_jobs || []).filter((j) => j.status === "failed").length;
  const runningCount = (stats?.recent_jobs || []).filter((j) => j.status === "processing" || j.status === "pending").length;

  const handleStopAllRunning = async () => {
    if (stopping) return;
    if (!window.confirm(`Stop ${runningCount} running job${runningCount === 1 ? "" : "s"} in this organization? In-flight scrape requests already sent to LeadFlow Scraper may still consume credits, but the pipeline will abort cleanly at the next stage.`)) {
      return;
    }
    setStopping(true);
    try {
      const res = await axios.post(`${API}/jobs/cancel-all-running`, {}, {
        headers: getAuthHeaders(),
        timeout: 60000,
      });
      const r = res.data || {};
      if (r.cancelled > 0) {
        toast.success(`Stopped ${r.cancelled} running job${r.cancelled === 1 ? "" : "s"}.`);
      } else {
        toast.info("No running jobs found.");
      }
      setTimeout(fetchStats, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to stop jobs");
    } finally {
      setStopping(false);
    }
  };

  const handleRetryAllFailed = async () => {
    if (retrying) return;
    if (!window.confirm(`Restart all failed jobs in this organization? This will re-run the full pipeline for every failed job (uses LeadFlow Scraper credits).`)) {
      return;
    }
    setRetrying(true);
    try {
      const res = await axios.post(`${API}/jobs/retry-failed`, {}, {
        headers: getAuthHeaders(),
        timeout: 60000,
      });
      const r = res.data || {};
      if (r.restarted > 0) {
        toast.success(`Restarted ${r.restarted} failed job${r.restarted === 1 ? "" : "s"}. They'll show as Processing in a moment.`);
      } else {
        toast.info("No failed jobs found to restart in this organization.");
      }
      // Refresh stats so the UI flips Failed → Processing
      setTimeout(fetchStats, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to restart jobs");
    } finally {
      setRetrying(false);
    }
  };

  const handleRestartJob = async (job) => {
    if (!job?.id) return;
    if (!window.confirm(`Restart job "${job.name || job.id}"? This re-runs the pipeline from where it stopped (or from scratch if no leads scraped yet).`)) {
      return;
    }
    try {
      await axios.post(`${API}/jobs/${job.id}/resume`, {}, {
        headers: getAuthHeaders(),
        timeout: 60000,
      });
      toast.success(`Job "${job.name || job.id}" restarted. It'll show as Processing in a moment.`);
      setTimeout(fetchStats, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to restart job");
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (job) => {
    setDeleteDialog({ open: true, job });
    setDeleteConfirmText("");
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, job: null });
    setDeleteConfirmText("");
  };

  const handleDeleteJob = async () => {
    if (!deleteDialog.job) return;
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/jobs/${deleteDialog.job.id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Job deleted successfully");
      closeDeleteDialog();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete job. Admin access required.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#006994] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Overview of your lead generation activities</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {runningCount > 0 && (
            <Button
              onClick={handleStopAllRunning}
              disabled={stopping}
              variant="outline"
              className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-sm"
              data-testid="stop-running-btn"
              title="Cancel every processing / pending job in this organization"
            >
              {stopping ? (
                <Spinner size={16} className="mr-2 animate-spin" />
              ) : (
                <XCircle size={16} className="mr-2" weight="bold" />
              )}
              Stop {runningCount} Running Job{runningCount === 1 ? "" : "s"}
            </Button>
          )}
          {failedCount > 0 && (
            <Button
              onClick={handleRetryAllFailed}
              disabled={retrying}
              variant="outline"
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 rounded-sm"
              data-testid="retry-failed-btn"
              title="Re-run every failed job in this organization"
            >
              {retrying ? (
                <Spinner size={16} className="mr-2 animate-spin" />
              ) : (
                <ArrowsClockwise size={16} className="mr-2" weight="bold" />
              )}
              Restart {failedCount} Failed Job{failedCount === 1 ? "" : "s"}
            </Button>
          )}
          <Button
            onClick={() => navigate("/jobs/new")}
            className="bg-[#006994] hover:bg-[#005A80] text-white font-bold rounded-sm "
            data-testid="new-job-btn"
          >
            <Plus size={18} className="mr-2" />
            New Lead Job
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Briefcase}
          label="Total Jobs"
          value={stats?.total_jobs || 0}
          color="bg-[#006994]/15 text-[#004a68]"
          delay={0}
        />
        <StatCard
          icon={Users}
          label="Total Leads"
          value={stats?.total_leads || 0}
          color="bg-[#0EA5E9]/15 text-[#0369a1]"
          delay={0.1}
        />
        <StatCard
          icon={CheckCircle}
          label="Verified Leads"
          value={stats?.verified_leads || 0}
          color="bg-emerald-100 text-emerald-700"
          delay={0.2}
        />
        <StatCard
          icon={DownloadSimple}
          label="Total Exports"
          value={stats?.total_exports || 0}
          color="bg-amber-100 text-amber-700"
          delay={0.3}
        />
      </div>

      {/* Recent Jobs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
      >
        <Card className="glass-card border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-bold text-slate-900">
              {showAllJobs ? "All Jobs" : "Recent Jobs"}
            </CardTitle>
            <button
              onClick={() => setShowAllJobs(!showAllJobs)}
              className="text-[#006994] text-sm font-medium hover:text-[#005A80] flex items-center gap-1"
              data-testid="view-all-jobs-link"
            >
              {showAllJobs ? "Show Recent" : `View All (${stats?.recent_jobs?.length || 0})`} <ArrowRight size={16} />
            </button>
          </CardHeader>
          <CardContent>
            {stats?.recent_jobs?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Job Name</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Category</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Leads</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllJobs ? stats.recent_jobs : stats.recent_jobs.slice(0, 5)).map((job, index) => {
                      const cats = (job.category || "").split(",").map(c => c.trim()).filter(Boolean);
                      const catPreview = cats.slice(0, 3).join(", ");
                      const catExtra = cats.length > 3 ? ` +${cats.length - 3} more` : "";
                      return (
                      <tr key={job.id} className="border-b border-slate-200 hover:bg-slate-100/30 transition-colors">
                        <td className="py-3 px-4 text-sm text-slate-900 font-medium">{job.name}</td>
                        <td className="py-3 px-4 text-sm text-slate-500 max-w-xs" title={job.category}>
                          <span className="line-clamp-1">
                            {catPreview}
                            {catExtra && <span className="text-slate-500">{catExtra}</span>}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={job.status} />
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {job.cleaned_leads_count || job.raw_leads_count || 0}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <Link
                              to={`/jobs/${job.id}`}
                              className="text-[#006994] text-sm font-medium hover:text-[#005A80]"
                              data-testid={`view-job-${job.id}`}
                            >
                              View
                            </Link>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="text-slate-500 hover:text-slate-900 p-1 rounded transition-colors"
                                  data-testid={`job-actions-${job.id}`}
                                  title="More actions"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <DotsThree size={20} weight="bold" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                <DropdownMenuItem
                                  onClick={() => navigate("/jobs/new", { state: { duplicateFrom: job } })}
                                  className="text-slate-700 focus:bg-slate-100 focus:text-[#0EA5E9] cursor-pointer"
                                  data-testid={`duplicate-job-${job.id}`}
                                >
                                  <Copy size={15} className="mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                {(user?.role === "admin" || user?.role === "super_admin") && (
                                  <>
                                    {job.status !== "completed" && (
                                      <DropdownMenuItem
                                        onClick={() => handleRestartJob(job)}
                                        className="text-[#006994] focus:bg-[#006994]/10 focus:text-[#005A80] cursor-pointer"
                                        data-testid={`restart-job-${job.id}`}
                                      >
                                        <ArrowsClockwise size={15} className="mr-2" />
                                        Restart
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator className="bg-slate-100" />
                                    <DropdownMenuItem
                                      onClick={() => openDeleteDialog(job)}
                                      className="text-red-400 focus:bg-red-500/10 focus:text-red-300 cursor-pointer"
                                      data-testid={`delete-job-${job.id}`}
                                    >
                                      <Trash size={15} className="mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Briefcase size={48} className="mx-auto text-slate-400 mb-4" />
                <p className="text-slate-500">No jobs yet. Create your first lead job!</p>
                <Button
                  onClick={() => navigate("/jobs/new")}
                  className="mt-4 bg-[#006994] hover:bg-[#005A80] text-white font-bold rounded-sm"
                  data-testid="create-first-job-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Create Job
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Warning size={24} className="text-red-500" />
              Delete Job
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              This action cannot be undone. This will permanently delete the job
              <span className="text-slate-900 font-medium"> &ldquo;{deleteDialog.job?.name}&rdquo;</span> and all its leads.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-2">
              Type <span className="text-red-400 font-mono font-bold">DELETE</span> to confirm:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
              placeholder="Type DELETE"
              className="bg-white/50 border-slate-200 text-slate-900 font-mono"
              data-testid="delete-confirm-input"
            />
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeDeleteDialog}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteJob}
              disabled={deleting || deleteConfirmText !== "DELETE"}
              className="bg-red-600 hover:bg-red-700 text-slate-900"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : "Delete Job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
